import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { TYPE_LABEL } from "@/types/movie";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) {
    return (
      <section className="h-[420px] md:h-[600px] bg-primary-950 flex items-center justify-center">
        <p className="text-background-300">暂无推荐影视</p>
      </section>
    );
  }

  const go = (next: number) => {
    setIndex((next + movies.length) % movies.length);
  };

  return (
    <section className="relative w-full h-[460px] md:h-[620px] overflow-hidden bg-foreground-950">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={movie.still}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 缘之空影视详情及剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/40"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex items-center">
        <div key={index} className="max-w-xl animate-fade-up">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
              {TYPE_LABEL[movies[index].type]}
            </span>
            <span className="text-background-200 text-sm">{movies[index].genres.join(" · ")}</span>
          </div>
          <h1 className="font-heading font-black text-background-50 text-3xl md:text-5xl leading-tight">
            {movies[index].title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400"></i>
              {movies[index].rating.toFixed(1)}
            </span>
            <span>{movies[index].region}</span>
            <span>{movies[index].year}</span>
            <span>{movies[index].duration}</span>
          </div>
          <p className="mt-5 text-background-200/90 text-sm md:text-base leading-relaxed line-clamp-3 max-w-lg">
            {movies[index].synopsis}
          </p>
          <div className="mt-7 flex items-center gap-3">
            <Link
              to={`/detail/${movies[index].id}`}
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              查看剧情详情
            </Link>
            <Link
              to={`/category/${movies[index].type}`}
              className="px-6 py-3 rounded-md border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors"
            >
              更多{TYPE_LABEL[movies[index].type]}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-0 right-0 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 flex items-center gap-2">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => go(i)}
              className={`h-1 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}